// #region Imports
import { createContext, useCallback, useContext, useMemo } from 'react';
import { GoMotoComponent, GoMotoFunction } from '../../API/GoMoto.types';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import { formatForServer } from '../../helpers/dateTime';
import store from 'store2';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import {
    ApiCallHook,
    ApiFunctionProps,
    ApiFunctionReturn,
    AxiosAuthInstanceHook,
    AxiosContextState,
    AxiosInstanceHook,
    AxiosProviderProps,
    AxiosState,
    AxiosStateContextType,
    LoginRequest,
    LoginToken,
} from './Axios.types';
import { APIError } from '../Admin/admin.types';
import { useLocalization } from '../Localization/localization';
// #endregion

// #region Setup
/**
 * Defines the initial state object of the Axios context with all properties set to undefined.
 */
const initialState: AxiosContextState = {
    instance: undefined,
    authInstance: undefined,
};

/**
 * Sets up the Axios state context and defaults it to undefined so that if any Axios context hook is used outside of the
 * Axios state context, we can throw an error.
 */
export const AxiosStateContext: AxiosStateContextType = createContext<AxiosState>(undefined);
// #endregion

// #region Provider
/**
 * Provides the Axios state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link AxiosProviderProps children} description.
 */
export const AxiosProvider: GoMotoComponent<AxiosProviderProps> = ({
    children,
}) => {
    // const { appApiRoot, appApiUsername, appApiKey } = useDevelopmentState();

    const axiosSuccessResponseInterceptor: GoMotoFunction<AxiosResponse<unknown, unknown>, AxiosResponse<unknown, unknown>> = useCallback((response) => response, []);

    const axiosErrorReponseInterceptor: GoMotoFunction<[AxiosError, AxiosInstance], Promise<unknown>> = useCallback((error, axiosInstance) => {
        if (error.response && error.response.data) {
            console.error(
                '-- Axios Error (API Error) -- \n' +
                (error.response.config && error.response.config.url ? error.response.config.url : '') +
                '\n' +
                JSON.stringify(error.response.data),
            );
        }
        else {
            const originalRequest: InternalAxiosRequestConfig<unknown> | undefined = error.config;

            console.error(
                '-- Axios Error (Network Error) -- \n' +
                (error.config && error.config.url ? error.config.url : '') +
                '\n' +
                JSON.stringify(error),
            );

            /**
             * If we encounter a network error, reattempt the call every 10 seconds
             * until any network issues have resolved themselves
             */
            if (!error.response && error.code !== AxiosError.ERR_CANCELED && !!originalRequest) {
                return new Promise((resolve, _reject) => {
                    setTimeout(() => resolve(axiosInstance(originalRequest)), 10000);
                });
            }
        }

        return Promise.reject(error);
    }, []);

    /**
     * Axios instance for use to obtain JWT from GoMotoCore
     */
    const authInstance = useMemo<AxiosInstance>(() => {
        const axiosAuthInstance: AxiosInstance = axios.create({
            baseURL: /* appApiRoot ??*/ process.env.REACT_APP_GOMOTO_API_ROOT,
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
            },
        });

        axiosAuthInstance.interceptors.response.use(axiosSuccessResponseInterceptor, (error: AxiosError) => axiosErrorReponseInterceptor(error, axiosAuthInstance));

        return axiosAuthInstance;
    }, [axiosErrorReponseInterceptor, axiosSuccessResponseInterceptor]);

    const Authorize: GoMotoFunction<[string?, string?], Promise<LoginToken>> = useCallback(async (username, key) => {
        const params: LoginRequest = {
            Username: username ?? process.env.REACT_APP_GOMOTO_API_USERNAME,
            Password: key ?? process.env.REACT_APP_GOMOTO_API_KEY,
        };

        return (await authInstance.post<LoginToken>('/Login', params)).data;
    }, [authInstance]);

    const instance = useMemo<AxiosInstance>(() => {
        const axiosInstance: AxiosInstance = axios.create({
            baseURL: ( /* appApiRoot ??*/ process.env.REACT_APP_GOMOTO_API_ROOT) + '/Kiosk',
            headers: {
                accept: 'application/json',
            },
            paramsSerializer: (params: Record<string, unknown>) => {
                return qs.stringify(params, {
                    arrayFormat: 'repeat',
                    serializeDate: formatForServer,
                });
            },
        });

        const formatObjectDates: GoMotoFunction<[Record<string, unknown>], Record<string, unknown> | unknown> = (object) => {
            if (typeof object === 'object') {
                Object.keys(object)
                    .forEach((key) => {
                        if (object[key] instanceof Date) {
                            object[key] = formatForServer(object[key]);
                        }
                    });
            }

            return object;

        };

        axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig<unknown>) => {
            const { headers } = config;
            let { data } = config;
            let token: unknown = store('token');

            if (!token) {
                token = (await Authorize(/* appApiUsername, appApiKey*/)).token;
                store('token', token);
            }

            token && (headers['Authorization'] = `Bearer ${token}`);

            if ((config.method === 'POST' || config.method === 'post') && typeof data === 'object') {
                data = formatObjectDates({ ...data });
            }

            return {
                ...config,
                data,
            };
        });

        const refreshAuthLogic: GoMotoFunction<AxiosError, Promise<void>> = (failedRequest) =>
            Authorize()
                .then((tokenRefreshResponse) => {
                    store('token', tokenRefreshResponse.token);

                    if (!!failedRequest && !!failedRequest.response) {
                        failedRequest.response.config.headers['Authorization'] = `Bearer ${tokenRefreshResponse.token}`;
                    }

                    return Promise.resolve();
                });

        axiosInstance.interceptors.response.use(axiosSuccessResponseInterceptor, (error: AxiosError) => axiosErrorReponseInterceptor(error, axiosInstance));

        createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic);

        return axiosInstance;
    }, [Authorize, axiosErrorReponseInterceptor, axiosSuccessResponseInterceptor]);

    return (
        <AxiosStateContext.Provider value={{
            ...initialState,
            instance: instance,
            authInstance: authInstance,
        }}
        >
            {children}
        </AxiosStateContext.Provider>
    );
};
// #endregion

// #region useAxiosInstance
/**
 * This hook returns the Axios instance that is used for API calls.
 */
export const useAxiosInstance: AxiosInstanceHook = () => {
    const context: AxiosState = useContext(AxiosStateContext);

    if (!context || !context.instance) {
        throw new Error('useAxiosInstance must be used within a AxiosProvider');
    }

    return context.instance;
};
// #endregion

// #region useAxiosAuthInstance
/**
 * Axios instance for use to obtain JWT from GoMotoCore
 */
export const useAxiosAuthInstance: AxiosAuthInstanceHook = () => {
    const context: AxiosState = useContext(AxiosStateContext);

    if (!context || !context.authInstance) {
        throw new Error('useAxiosAuthInstance must be used within a AxiosProvider');
    }

    return context.authInstance;
};
// #endregion

// #region useApiCall
/**
 * This hook handles logic for making API calls through the Axios instance. It returns the `api` function which
 * takes in various functions for logic before, during, and after the API call and returns the API call request types
 * as functions to call.
 */
export const useApiCall: ApiCallHook = () => {
    const instance: AxiosInstance = useAxiosInstance();
    const Localization = useLocalization();

    return {
        api: useCallback(<Type,>({
            onSuccess,
            onLoading,
            onError,
            onComplete,
            onCanceledError,
        }: ApiFunctionProps<Type>): ApiFunctionReturn => {
            const LoadingHandler: VoidFunction = () => {
                if (onLoading) {
                    onLoading(true);
                }
            };

            const SuccessHandler: GoMotoFunction<[AxiosResponse<Type>], void> = (response) => {
                if (onSuccess) {
                    onSuccess(response.data);
                }
            };

            const ErrorHandler: GoMotoFunction<[AxiosError<APIError>], void> = (error) => {
                let canceled: boolean = error && error.code === 'ERR_CANCELED';

                if (canceled && onCanceledError) {
                    onCanceledError();
                }
                else if (!canceled && onError) {
                    onError(
                        error?.response?.data?.message ??
                        error?.message ??
                        Localization.UnexpectedError,
                    );
                }
            };

            const CompleteHandler: VoidFunction = () => {
                if (onLoading) {
                    onLoading(false);
                }

                if (onComplete) {
                    onComplete();
                }
            };

            return {
                get: (url: string, options: AxiosRequestConfig | undefined = undefined): void => {
                    LoadingHandler();
                    instance.get<Type>(url, options)
                        .then(SuccessHandler)
                        .catch(ErrorHandler)
                        .finally(CompleteHandler);
                },
                put: (url: string, data?: any): void => {
                    LoadingHandler();
                    instance.put<Type>(url, data)
                        .then(SuccessHandler)
                        .catch(ErrorHandler)
                        .finally(CompleteHandler);
                },
                post: (url: string, data?: any): void => {
                    LoadingHandler();
                    instance.post<Type>(url, data)
                        .then(SuccessHandler)
                        .catch(ErrorHandler)
                        .finally(CompleteHandler);
                },
                delete: (url: string, data?: any): void => {
                    LoadingHandler();
                    instance.delete<Type>(url, data)
                        .then(SuccessHandler)
                        .catch(ErrorHandler)
                        .finally(CompleteHandler);
                },
            };
        }, [Localization.UnexpectedError, instance]),
    };
};
// #endregion